export interface MenuPermissionItem {
	id?: number;
	name: string;
	is_assigned?: boolean;
	children?: MenuPermissionItem[];
}

export interface MenuPermissionUser {
	is_superuser?: boolean;
	role?: number;
	roles?: number[];
	effective_menu_items?: MenuPermissionItem[];
}

const SYSTEM_ADMINISTRATOR_ROLE = 1;

// Organization Settings should be as fully accessible to any System
// Administrator (role 1) as it already is to the single is_superuser
// account, regardless of that individual user's own menu-item snapshot.
// Scoped to this one menu tree so it doesn't widen access elsewhere.
const SYSTEM_ADMINISTRATOR_BYPASS_MENUS = ['Organization Settings'];

export const isSystemAdministrator = (user: MenuPermissionUser | null | undefined) => {
	const roles = user?.roles?.length ? user.roles : (user?.role ? [user.role] : []);
	return roles.includes(SYSTEM_ADMINISTRATOR_ROLE);
};

const normalizeMenuName = (value: string) => value.trim().toLowerCase();

const anyDescendantAssigned = (item: MenuPermissionItem): boolean => {
	if (item.is_assigned) return true;
	if (item.children) {
		return item.children.some((child) => anyDescendantAssigned(child));
	}
	return false;
};

const hasMenuPath = (items: MenuPermissionItem[] = [], path: string[]) => {
	const [current, ...rest] = path;
	if (!current) return true;

	return items.some((item) => {
		if (normalizeMenuName(item.name) !== normalizeMenuName(current)) return false;
		if (!rest.length) return item.is_assigned === true;
		return hasMenuPath(item.children || [], rest);
	});
};

const hasMenuPermission = (items: MenuPermissionItem[] = [], menuName: string) => {
	const path = menuName.split('/').map((part) => part.trim()).filter(Boolean);
	if (path.length > 1) {
		return hasMenuPath(items, path);
	}
	// Single-level: check if the item is assigned OR any descendant is assigned.
	// This lets parent nav items show when any child is assigned.
	return items.some((item) => {
		if (normalizeMenuName(item.name) !== normalizeMenuName(menuName)) return false;
		return anyDescendantAssigned(item);
	});
};

export const canAccessMenu = (
	user: MenuPermissionUser | null | undefined,
	requiredMenu?: string | string[],
) => {
	if (!requiredMenu) return true;

	// Superusers have access to everything regardless of menu item assignments
	if (user?.is_superuser) return true;

	const requiredMenus = Array.isArray(requiredMenu) ? requiredMenu : [requiredMenu];
	if (!requiredMenus.length) return true;

	if (
		isSystemAdministrator(user) &&
		requiredMenus.some((menuName) => SYSTEM_ADMINISTRATOR_BYPASS_MENUS.some(
			(allowed) => normalizeMenuName(menuName) === normalizeMenuName(allowed)
				|| normalizeMenuName(menuName).startsWith(`${normalizeMenuName(allowed)}/`),
		))
	) {
		return true;
	}

	const menuItems = user?.effective_menu_items || [];
	return requiredMenus.some((menuName) => hasMenuPermission(menuItems, menuName));
};

export const filterProtectedTabs = <T extends { key?: string; label?: unknown; menuName?: string }>(
	items: T[],
	user: MenuPermissionUser | null | undefined,
) => items.filter((item) => canAccessMenu(user, item.menuName || (typeof item.label === 'string' ? item.label : undefined)));
